// TenantPaymentForm.jsx
// Stripe Elements payment form for tenant subscriptions

import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    Alert,
    Paper,
    Divider,
    Radio,
    RadioGroup,
    FormControlLabel,
} from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { loadStripe } from '@stripe/stripe-js';
import {
    Elements,
    CardElement,
    useStripe,
    useElements,
} from '@stripe/react-stripe-js';
import PropTypes from 'prop-types';
import theme from '../../theme';

// Stripe configuration
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
const STRIPE_SERVER_URL = process.env.REACT_APP_STRIPE_SERVER_URL ;


// Styles
const styles = {
    container: {
        padding: '24px',
        borderRadius: '12px',
        backgroundColor: '#fff',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    },
    title: {
        fontWeight: 600,
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    priceBox: {
        backgroundColor: 'rgba(129, 194, 65, 0.08)',
        padding: '16px',
        borderRadius: '8px',
        marginBottom: '24px',
        textAlign: 'center',
    },
    price: {
        fontSize: '32px',
        fontWeight: 700,
        color: theme.colors?.green || '#81c241',
    },
    pricePeriod: {
        color: theme.colors?.mediumGray || '#666',
        fontSize: '14px',
    },
    cardElement: {
        padding: '16px',
        border: '1px solid #f9f9f9',
        borderRadius: '8px',
        backgroundColor: '#f9f9f9',
        marginBottom: '16px',
    },
    savedMethodsContainer: {
        marginBottom: '24px',
    },
    savedMethodCard: {
        display: 'flex',
        alignItems: 'center',
        padding: '12px 16px',
        border: '1px solid #f9f9f9',
        borderRadius: '8px',
        marginBottom: '8px',
        cursor: 'pointer',
        transition: 'all 0.2s',
        '&:hover': {
            borderColor: theme.colors?.green || '#81c241',
            backgroundColor: 'rgba(129, 194, 65, 0.04)',
        },
    },
    selectedMethod: {
        borderColor: theme.colors?.green || '#81c241',
        backgroundColor: 'rgba(129, 194, 65, 0.08)',
    },
    cardBrand: {
        fontWeight: 600,
        marginRight: '8px',
    },
    cardLast4: {
        color: theme.colors?.mediumGray || '#666',
    },
    submitButton: {
        padding: '14px',
        borderRadius: '8px',
        backgroundColor: theme.colors?.green || '#81c241',
        color: '#fff',
        fontWeight: 600,
        fontSize: '16px',
        textTransform: 'none',
        '&:hover': {
            backgroundColor: theme.colors?.greenHover || '#6aa832',
        },
        '&:disabled': {
            backgroundColor: '#f9f9f9',
        },
    },
    secureText: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '4px',
        marginTop: '16px',
        color: theme.colors?.mediumGray || '#666',
        fontSize: '12px',
    },
};

// Card Element options
const cardElementOptions = {
    style: {
        base: {
            fontSize: '16px',
            color: '#333333',
            fontFamily: '"Roboto", sans-serif',
            '::placeholder': {
                color: '#666666',
            },
        },
        invalid: {
            color: '#333333',
            iconColor: '#333333',
        },
    },
    hidePostalCode: true,
};

// Payment Form Component (wrapped in Elements)
const PaymentFormContent = ({
    customerId,
    email,
    propertyId,
    managerId,
    onSuccess,
    onError,
}) => {
    const stripe = useStripe();
    const elements = useElements();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [savedMethods, setSavedMethods] = useState([]);
    const [selectedMethod, setSelectedMethod] = useState('new');
    const [loadingMethods, setLoadingMethods] = useState(true);

    // Fetch saved payment methods
    useEffect(() => {
        const fetchMethods = async () => {
            if (!customerId) {
                setLoadingMethods(false);
                return;
            }

            try {
                const response = await fetch(
                    `${STRIPE_SERVER_URL}/api/payments/saved-methods?customerId=${customerId}`
                );
                const data = await response.json();

                if (data.paymentMethods && data.paymentMethods.length > 0) {
                    setSavedMethods(data.paymentMethods);
                    setSelectedMethod(data.paymentMethods[0].id);
                }
            } catch (err) {
                console.error('Error fetching saved methods:', err);
            } finally {
                setLoadingMethods(false);
            }
        };

        fetchMethods();
    }, [customerId]);

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!stripe || !elements) {
            return;
        }

        setLoading(true);
        setError(null);

        try {
            // If using a saved payment method
            if (selectedMethod !== 'new' && savedMethods.length > 0) {
                const response = await fetch(`${STRIPE_SERVER_URL}/api/subscribe/tenant`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        tenantEmail: email,
                        propertyId,
                        managerId,
                        paymentMethodId: selectedMethod,
                    }),
                });

                const data = await response.json();

                if (data.url) {
                    window.location.href = data.url;
                } else if (data.sessionId) {
                    await stripe.redirectToCheckout({ sessionId: data.sessionId });
                } else {
                    throw new Error(data.error || 'Failed to create subscription');
                }
            } else {
                // Create checkout session with new card
                const response = await fetch(`${STRIPE_SERVER_URL}/api/subscribe/tenant`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        tenantEmail: email,
                        propertyId,
                        managerId,
                    }),
                });

                const data = await response.json();

                if (data.url) {
                    window.location.href = data.url;
                } else if (data.sessionId) {
                    await stripe.redirectToCheckout({ sessionId: data.sessionId });
                } else {
                    throw new Error(data.error || 'Failed to create subscription');
                }
            }

            if (onSuccess) onSuccess();
        } catch (err) {
            console.error('Payment error:', err);
            setError(err.message || 'Payment failed. Please try again.');
            if (onError) onError(err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Paper sx={styles.container}>
            <Typography variant="h6" sx={styles.title}>
                <CreditCardIcon /> Subscribe to Property Access
            </Typography>

            {/* Price Display */}
            <Box sx={styles.priceBox}>
                <Typography sx={styles.price}>
                    $5.99
                    <Typography component="span" sx={styles.pricePeriod}>/month</Typography>
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    Cancel anytime • No hidden fees
                </Typography>
            </Box>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <form onSubmit={handleSubmit}>
                {/* Saved Payment Methods */}
                {!loadingMethods && savedMethods.length > 0 && (
                    <Box sx={styles.savedMethodsContainer}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Saved Payment Methods
                        </Typography>
                        <RadioGroup
                            value={selectedMethod}
                            onChange={(e) => setSelectedMethod(e.target.value)}
                        >
                            {savedMethods.map((method) => (
                                <FormControlLabel
                                    key={method.id}
                                    value={method.id}
                                    control={<Radio size="small" />}
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Typography sx={styles.cardBrand}>
                                                {method.card.brand.toUpperCase()}
                                            </Typography>
                                            <Typography sx={styles.cardLast4}>
                                                •••• {method.card.last4}
                                            </Typography>
                                        </Box>
                                    }
                                    sx={{
                                        ...styles.savedMethodCard,
                                        ...(selectedMethod === method.id ? styles.selectedMethod : {}),
                                    }}
                                />
                            ))}
                            <FormControlLabel
                                value="new"
                                control={<Radio size="small" />}
                                label="Use a new card"
                                sx={styles.savedMethodCard}
                            />
                        </RadioGroup>
                    </Box>
                )}

                {/* New Card Input */}
                {(selectedMethod === 'new' || savedMethods.length === 0) && (
                    <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Card Details
                        </Typography>
                        <Box sx={styles.cardElement}>
                            <CardElement options={cardElementOptions} />
                        </Box>
                    </Box>
                )}

                <Divider sx={{ my: 2 }} />

                {/* Submit Button */}
                <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={!stripe || loading}
                    sx={styles.submitButton}
                >
                    {loading ? (
                        <CircularProgress size={24} color="inherit" />
                    ) : (
                        'Subscribe for $5.99/month'
                    )}
                </Button>

                {/* Secure Payment Note */}
                <Typography sx={styles.secureText}>
                    <CheckCircleIcon fontSize="small" />
                    Secured by Stripe. Your card details are encrypted.
                </Typography>
            </form>
        </Paper>
    );
};

PaymentFormContent.propTypes = {
    customerId: PropTypes.string,
    email: PropTypes.string.isRequired,
    propertyId: PropTypes.string.isRequired,
    managerId: PropTypes.string,
    onSuccess: PropTypes.func,
    onError: PropTypes.func,
};

// Main Component with Elements wrapper
const TenantPaymentForm = (props) => {
    return (
        <Elements stripe={stripePromise}>
            <PaymentFormContent {...props} />
        </Elements>
    );
};

export default TenantPaymentForm;
